import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  LayoutDashboard,
  Receipt,
  PiggyBank,
  Target,
  Repeat,
  FileText,
  FileSpreadsheet,
  Tags,
  Settings,
  LogOut,
  TrendingUp,
  Lock,
  HelpCircle,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSubscription } from "@/hooks/use-subscription";
import { useOnboarding } from "@/hooks/use-onboarding";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";

const LOCKED_FEATURES: Record<string, string> = {
  "/budget": "budget_planning",
  "/dre": "dre_reports",
};

const mainNavItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Transações", url: "/transactions", icon: Receipt },
  { title: "Planejamento", url: "/budget", icon: PiggyBank },
  { title: "Cofrinhos", url: "/goals", icon: Target },
  { title: "Recorrentes", url: "/recurring", icon: Repeat },
  { title: "Relatórios", url: "/reports", icon: FileText },
  { title: "DRE", url: "/dre", icon: FileSpreadsheet },
  { title: "Categorias", url: "/categories", icon: Tags },
];

const footerNavItems = [
  { title: "Configurações", url: "/settings", icon: Settings },
];

const PLAN_BADGE_MAP: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  freemium: { label: "Freemium", variant: "outline" },
  starter: { label: "Starter", variant: "secondary" },
  colab: { label: "Colab", variant: "default" },
};

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { canAccess, planTier, isTrial, trialEndsAt, cancelAtPeriodEnd, currentPeriodEnd } = useSubscription();
  const { updateOnboarding } = useOnboarding();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const isActive = (path: string) => location.pathname === path;

  const isLocked = (url: string): boolean => {
    const featureKey = LOCKED_FEATURES[url];
    if (!featureKey) return false;
    return !canAccess(featureKey);
  };

  const handleNavClick = (e: React.MouseEvent, url: string) => {
    if (isLocked(url)) {
      e.preventDefault();
      e.stopPropagation();
      setShowUpgradeModal(true);
    }
  };

  const handleReplayTour = async () => {
    await updateOnboarding({ has_completed_tour: false });
    window.location.reload();
  };

  const planBadge = PLAN_BADGE_MAP[planTier] ?? PLAN_BADGE_MAP.freemium;

  const getBadgeTooltip = (): string | null => {
    if (isTrial && trialEndsAt) {
      const dateStr = trialEndsAt.toLocaleDateString("pt-BR");
      if (cancelAtPeriodEnd) {
        return `Teste gratuito cancelado — acesso até ${dateStr}`;
      }
      return `Período de teste gratuito até ${dateStr}`;
    }
    if (cancelAtPeriodEnd && currentPeriodEnd) {
      return `Cancelado — acesso até ${currentPeriodEnd.toLocaleDateString("pt-BR")}`;
    }
    return null;
  };

  const badgeTooltip = getBadgeTooltip();

  return (
    <>
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-2 px-2 py-3">
          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center shrink-0">
            <TrendingUp className="h-5 w-5 text-primary-foreground" />
          </div>
          {!collapsed && (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-bold text-lg truncate">Next AI Finance</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge variant={planBadge.variant} className="text-[10px] px-1.5 py-0 h-5 shrink-0 cursor-help">
                      {planBadge.label}
                    </Badge>
                  </span>
                </TooltipTrigger>
                {badgeTooltip && (
                  <TooltipContent side="bottom">
                    <p>{badgeTooltip}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup data-tour="sidebar-nav">
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => {
                const locked = isLocked(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                    >
                      <NavLink
                        to={item.url}
                        className="flex items-center gap-2"
                        onClick={(e) => handleNavClick(e, item.url)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="flex-1">{item.title}</span>
                        {locked && (
                          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          {footerNavItems.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={isActive(item.url)}
                tooltip={item.title}
              >
                <NavLink
                  to={item.url}
                  className="flex items-center gap-2"
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.title}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleReplayTour}
              tooltip="Replay tour"
            >
              <HelpCircle className="h-4 w-4" />
              <span>Tour do App</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              tooltip="Sair"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              <span>Sair</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>

    <UpgradeModal
      externalOpen={showUpgradeModal}
      onExternalClose={() => setShowUpgradeModal(false)}
    />
    </>
  );
}
